import * as React from 'react';
import { FieldValues } from 'react-hook-form';
import { DataCy } from '../../../../types/dataAttributes';
import { JRCFormFieldRenderer } from '../Common/JRCFormFieldRenderer';
import { JRCInputFieldProps } from '../Common/JRCFormFieldRenderer.types';
import { InputTextStyled } from './JRCInputText.style';

export type JRCInputTextProps = DataCy &
    React.ComponentPropsWithoutRef<'input'> & {
        width?: string;
        autocomplete?: 'on' | 'off';
        inputmode?: 'text' | 'numeric';
    };

const RenderInputElement = (props: JRCInputTextProps, ref: React.ForwardedRef<HTMLInputElement>) => {
    return <InputTextStyled type="text" id={props.name} {...props} ref={ref} />;
};
export const RenderInput = React.forwardRef(RenderInputElement);

/**
 * Component used as a <input type="text"/>
 * @param props JRCInputFieldProps
 * validation props: required
 * @returns JSX.Element
 */
export function JRCInputText<T extends FieldValues = FieldValues>(
    props: JRCInputFieldProps<T> & {
        width?: string;
        disabled?: boolean;
        autoComplete?: 'on' | 'off';
        inputmode?: 'text' | 'numeric';
    },
) {
    return (
        <JRCFormFieldRenderer
            {...props}
            renderFunction={({ value, ...field }) => <RenderInput {...props} {...field} value={value as string} />}
        />
    );
}

export type JRCInputNumberProps = DataCy &
    React.ComponentPropsWithoutRef<'input'> & { width?: string; min?: number; max?: number };

const RenderNumberInputElement = (props: JRCInputNumberProps, ref: React.ForwardedRef<HTMLInputElement>) => {
    return <InputTextStyled type="number" id={props.name} {...props} ref={ref} />;
};
export const RenderNumberInput = React.forwardRef(RenderNumberInputElement);

/**
 * Component used as a <input type="number"/>
 * @param props JRCInputFieldProps
 * validation props: required
 * @returns JSX.Element
 */
export function JRCInputNumber<T extends FieldValues = FieldValues>(
    props: JRCInputFieldProps<T> & { width?: string; disabled?: boolean; min?: number; max?: number },
) {
    function handleChange(e: React.ChangeEvent<HTMLInputElement>, callback: (value: number) => void) {
        const value = e.target.value;
        if (value === '') {
            return;
        }
        const number = Number(value);
        if ((props.min !== undefined && number < props.min) || (props.max !== undefined && number > props.max)) {
            return;
        }
        callback(+e.target.value);
    }

    return (
        <JRCFormFieldRenderer
            {...props}
            renderFunction={({ value, ...field }) => (
                <RenderNumberInput
                    {...props}
                    {...field}
                    value={value as string}
                    onChange={(e) => handleChange(e, field.onChange)}
                />
            )}
        />
    );
}
